import {
    DESKTOP_LAYOUT_BREAKPOINT,
    PANEL_COLLAPSED_STORAGE_KEY,
} from '../core/constants';
import { createSheetGestureController } from './sheet-gestures';

export function createPanelLayout(config) {
    var appEl = config.appEl;
    var getMap = config.getMap;
    var html = config.html;
    var panelToggleBtn = config.panelToggleBtn;
    var sheetEl = config.sheetEl;
    var sheetHandleEl = config.sheetHandleEl;
    var topbarEl = config.topbarEl;

    var currentSnap = 'half';
    var panelCollapsed = localStorage.getItem(PANEL_COLLAPSED_STORAGE_KEY) === '1';
    var panelToggleTimer = null;
    var sheetBreaks = { full: 0, half: 0, peek: 116 };
    var sheetY = 0;
    var sheetContentEl = null;
    var halfHeightOverride = null;
    var gestureController = null;
    var sheetTransitionEnd = null;
    var sheetTransitionTimer = null;

    function isDesktopLayout() {
        return window.innerWidth >= DESKTOP_LAYOUT_BREAKPOINT;
    }

    function syncMapOverlayOffset() {
        if (!appEl) return;
        var bottomOffset = 12;
        var isFullSheet = !isDesktopLayout() && currentSnap === 'full';
        if (isDesktopLayout()) {
            bottomOffset = 18;
        } else if (sheetEl) {
            var sheetTop = sheetEl.getBoundingClientRect().top;
            var visibleSheetHeight = Math.max(0, window.innerHeight - sheetTop);
            bottomOffset = isFullSheet ? 12 : Math.round(visibleSheetHeight + 12);
        }
        appEl.classList.toggle('is-sheet-full', isFullSheet);
        appEl.style.setProperty('--nm-map-overlay-bottom', bottomOffset + 'px');
    }

    function scheduleMapResize() {
        if (panelToggleTimer) clearTimeout(panelToggleTimer);
        panelToggleTimer = setTimeout(function() {
            var map = getMap ? getMap() : null;
            if (map) map.resize();
        }, 320);
    }

    function applyPanelCollapseState(options) {
        options = options || {};
        var desktop = isDesktopLayout();
        if (appEl) appEl.classList.toggle('is-panel-collapsed', desktop && panelCollapsed);
        if (panelToggleBtn) {
            panelToggleBtn.hidden = !desktop;
            panelToggleBtn.setAttribute('aria-label', panelCollapsed ? 'Pokaż panel boczny' : 'Ukryj panel boczny');
            panelToggleBtn.setAttribute('aria-pressed', panelCollapsed ? 'true' : 'false');
        }
        if (options.persist !== false) {
            localStorage.setItem(PANEL_COLLAPSED_STORAGE_KEY, panelCollapsed ? '1' : '0');
        }
        if (options.invalidate !== false) scheduleMapResize();
    }

    function recalculateSheetBreaks() {
        if (!sheetEl) return;
        var viewportH = window.innerHeight;
        var topbarBottom = topbarEl ? topbarEl.getBoundingClientRect().bottom : 92;
        var fullTop = Math.min(Math.max(topbarBottom + 10, 88), Math.round(viewportH * 0.3));
        fullTop = Math.max(0, Math.min(fullTop, Math.max(0, viewportH - 144)));
        var sheetH = viewportH - fullTop;
        var defaultHalfH = window.innerWidth <= 640 ? 332 : 356;
        var halfH = halfHeightOverride != null ? halfHeightOverride : defaultHalfH;
        var snapGap = Math.min(28, sheetH / 3);
        var peekH = Math.min(116, sheetH - 2 * snapGap);
        halfH = Math.max(peekH + snapGap, Math.min(halfH, sheetH - snapGap));

        sheetEl.style.height = sheetH + 'px';
        sheetBreaks.full = 0;
        sheetBreaks.half = sheetH - halfH;
        sheetBreaks.peek = sheetH - peekH;
    }

    function readVisualSheetY() {
        if (!sheetEl) return sheetY;
        var transform = getComputedStyle(sheetEl).transform;
        if (!transform || transform === 'none') return sheetY;
        try {
            if (typeof DOMMatrixReadOnly !== 'undefined') {
                var y = new DOMMatrixReadOnly(transform).m42;
                if (Number.isFinite(y)) return y;
            }
            var match = transform.match(/^matrix(3d)?\((.+)\)$/);
            if (match) {
                var values = match[2].split(',').map(Number);
                var value = values[match[1] ? 13 : 5];
                if (Number.isFinite(value)) return value;
            }
        } catch (_) { /* fall back to the last known position */ }
        return sheetY;
    }

    function stopSheetAnimation() {
        if (!sheetEl) return;
        if (sheetTransitionEnd) {
            sheetEl.removeEventListener('transitionend', sheetTransitionEnd);
            sheetTransitionEnd = null;
        }
        if (sheetTransitionTimer) clearTimeout(sheetTransitionTimer);
        sheetTransitionTimer = null;
        sheetEl.style.transition = 'none';
    }

    function applySheetTransform(y) {
        if (!sheetEl) return;
        y = Math.max(0, Math.min(y, sheetBreaks.peek));
        sheetY = y;
        sheetEl.style.transform = 'translateY(' + y + 'px)';
    }

    function interruptSheetAnimation() {
        var y = readVisualSheetY();
        stopSheetAnimation();
        applySheetTransform(y);
        return sheetY;
    }

    function syncSheetClasses() {
        var mobile = !isDesktopLayout();
        [html, appEl].forEach(function(el) {
            if (!el) return;
            el.classList.toggle('is-pane-bottom', mobile && currentSnap === 'peek');
            el.classList.toggle('is-pane-top', mobile && currentSnap === 'full');
        });
    }

    function animateSheetTo(snap, options) {
        options = options || {};
        currentSnap = snap;
        syncSheetClasses();
        if (!sheetEl) return;
        if (isDesktopLayout()) {
            if (gestureController) gestureController.syncA11y();
            syncMapOverlayOffset();
            return;
        }
        var targetY = sheetBreaks[snap] != null ? sheetBreaks[snap] : sheetBreaks.half;
        var startY = readVisualSheetY();
        stopSheetAnimation();
        applySheetTransform(startY);
        var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var immediate = options.immediate || reducedMotion || Math.abs(startY - targetY) < 0.5;
        if (!immediate) {
            // Commit the interrupted position before starting a new transition.
            sheetEl.getBoundingClientRect();
            sheetEl.style.transition = 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)';
        }
        applySheetTransform(targetY);
        if (sheetContentEl) {
            sheetContentEl.style.overflowY = snap === 'full' ? 'auto' : 'hidden';
        }
        if (gestureController) gestureController.syncA11y();
        syncMapOverlayOffset();
        if (immediate) return;
        var onEnd = function(e) {
            if (e && (e.target !== sheetEl || e.propertyName !== 'transform')) return;
            if (sheetTransitionEnd !== onEnd) return;
            sheetEl.removeEventListener('transitionend', onEnd);
            sheetTransitionEnd = null;
            if (sheetTransitionTimer) clearTimeout(sheetTransitionTimer);
            sheetTransitionTimer = null;
            sheetEl.style.transition = '';
            syncMapOverlayOffset();
        };
        sheetTransitionEnd = onEnd;
        sheetEl.addEventListener('transitionend', onEnd);
        sheetTransitionTimer = setTimeout(function() { onEnd(); }, 420);
    }

    function syncLayoutMode() {
        if (!appEl) return;
        var desktop = isDesktopLayout();
        syncSheetClasses();
        appEl.classList.toggle('is-desktop', desktop);
        applyPanelCollapseState({ persist: false, invalidate: false });
        if (gestureController) gestureController.cancel();
        if (desktop) {
            stopSheetAnimation();
            if (sheetEl) sheetEl.style.transition = '';
            if (sheetContentEl) sheetContentEl.style.overflowY = 'auto';
            syncMapOverlayOffset();
            return;
        }
        recalculateSheetBreaks();
        animateSheetTo(currentSnap, { immediate: true });
    }

    function setSheetSnap(snap) {
        if (['full', 'half', 'peek'].indexOf(snap) === -1) snap = 'half';
        if (gestureController) gestureController.cancel();
        currentSnap = snap;
        if (isDesktopLayout()) {
            syncMapOverlayOffset();
            return;
        }
        recalculateSheetBreaks();
        animateSheetTo(snap);
    }

    function initSheetDrag() {
        if (!sheetEl) return;
        sheetEl.hidden = false;
        sheetContentEl = sheetEl.querySelector('.nm-sheet-content');
        if (!gestureController) {
            gestureController = createSheetGestureController({
                sheetEl: sheetEl,
                contentEl: sheetContentEl,
                handleEl: sheetHandleEl,
                isDesktopLayout: isDesktopLayout,
                getBreaks: function() { return sheetBreaks; },
                getCurrentSnap: function() { return currentSnap; },
                getSheetY: function() { return sheetY; },
                interrupt: interruptSheetAnimation,
                dragTo: function(y) {
                    applySheetTransform(y);
                    syncMapOverlayOffset();
                },
                snapTo: setSheetSnap,
            });
            gestureController.init();
        }
        if (!isDesktopLayout()) {
            recalculateSheetBreaks();
            animateSheetTo(currentSnap, { immediate: true });
        }
    }

    function getMapPaddingForPanel() {
        if (isDesktopLayout()) {
            var rightPad = 20;
            var map = getMap ? getMap() : null;
            if (!panelCollapsed && sheetEl && map && typeof map.getContainer === 'function') {
                var mapRect = map.getContainer().getBoundingClientRect();
                var panelRect = sheetEl.getBoundingClientRect();
                // The canvas extends behind the right-hand panel. Center the
                // camera in the unobstructed area, using its actual width.
                var coveredWidth = Math.max(0, mapRect.right - Math.max(mapRect.left, panelRect.left));
                rightPad += Math.min(coveredWidth, Math.max(0, mapRect.width - 120));
            }
            return { top: 80, bottom: 20, left: 20, right: rightPad };
        }
        var halfSnapH = sheetEl ? sheetEl.offsetHeight - sheetBreaks.half : 332;
        var sheetH = 0;
        if (sheetEl) {
            var sheetTop = sheetEl.getBoundingClientRect().top;
            sheetH = Math.max(0, window.innerHeight - sheetTop);
        }
        var bottomPad = Math.min(sheetH, halfSnapH) + 20;
        return { top: 80, bottom: bottomPad, left: 20, right: 20 };
    }

    function easeMapToKeepPointVisible(latlng) {
        var map = getMap ? getMap() : null;
        if (!map || !latlng || isDesktopLayout()) return;
        var point = map.project([latlng.lng, latlng.lat]);
        var panelTop = sheetEl ? sheetEl.getBoundingClientRect().top : window.innerHeight;
        var safeBottom = panelTop - 20;
        if (point.y > safeBottom) {
            var shiftPx = point.y - safeBottom + 40;
            map.panBy([0, shiftPx], { duration: 350 });
        }
    }

    function togglePanelCollapse() {
        if (!isDesktopLayout()) return;
        panelCollapsed = !panelCollapsed;
        applyPanelCollapseState();
    }

    function getCurrentSnap() {
        return currentSnap;
    }

    function setHalfSnapHeight(px) {
        halfHeightOverride = (px == null || px <= 0) ? null : Math.round(px);
        if (isDesktopLayout() || !sheetEl) return;
        // Keep geometry fixed under the finger; release recalculates the snaps
        // using the latest content height through setSheetSnap().
        if (gestureController && gestureController.isDragging()) return;
        recalculateSheetBreaks();
        if (currentSnap === 'half') {
            animateSheetTo('half');
        }
    }

    return {
        easeMapToKeepPointVisible: easeMapToKeepPointVisible,
        getCurrentSnap: getCurrentSnap,
        getMapPaddingForPanel: getMapPaddingForPanel,
        initSheetDrag: initSheetDrag,
        isDesktopLayout: isDesktopLayout,
        setHalfSnapHeight: setHalfSnapHeight,
        setSheetSnap: setSheetSnap,
        syncLayoutMode: syncLayoutMode,
        togglePanelCollapse: togglePanelCollapse,
    };
}
